/**
 * Team Mapping Service
 * Translates sprint names to team names using dynamic team configuration from backend API
 * Migrated from hardcoded arrays to API-driven configuration
 */

import { teamConfigService } from './teamConfigService'

// Cache for team names to avoid repeated API calls
let teamNamesCache: string[] | null = null
let cacheTimestamp: number = 0
const CACHE_DURATION = 10 * 60 * 1000 // 10 minutes

/**
 * Get team names from API with caching
 */
async function getTeamNamesFromAPI(): Promise<string[]> {
  const now = Date.now()

  // Return cached data if still valid
  if (teamNamesCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return teamNamesCache
  }

  try {
    const response = await teamConfigService.getTeamNames()
    if (response.success && response.data) {
      teamNamesCache = response.data
      cacheTimestamp = now
      return response.data
    }
  } catch (error) {
    console.warn('Failed to fetch team names from API, using fallback:', error)
  }

  // Fallback to basic team list if API fails
  return [
    'AI Services', 'AI/Search', 'Cloud IdM', 'CRDB', 'DBM', 'DevX_Infra',
    'InfraDevops Cloud', 'PC', 'RedisCore', 'RedisInsight', 'SM Core', 'ZTC'
  ]
}

/**
 * Get team name from sprint name - flexible matching approach
 * Now uses dynamic team configuration from backend API
 * @param sprintName - The sprint name from Jira
 * @returns Promise<string> - The team name, or the original sprint name if no match found
 */
export async function getTeamFromSprint(sprintName: string): Promise<string> {
  if (!sprintName || typeof sprintName !== 'string') {
    return 'Unknown Team'
  }

  const sprintLower = sprintName.toLowerCase().trim()
  const teamNames = await getTeamNamesFromAPI()

  // Handle AI/Search consolidation first (specific patterns)
  if (sprintLower.includes('ai epic backlog') || sprintLower.includes('search epic backlog')) {
    return 'AI/Search'
  }

  // Handle AI Services separately (must come before general AI matching)
  if (sprintLower.includes('ai services') || sprintLower.includes('ai-services') || sprintLower.includes('aiservices')) {
    return 'AI Services'
  }

  // Handle general AI or Search patterns (consolidate to AI/Search)
  if (sprintLower.includes('ai') || sprintLower.includes('search') || sprintLower.includes('artificial intelligence')) {
    // Make sure it's not AI Services (already handled above)
    if (!sprintLower.includes('ai services')) {
      return 'AI/Search'
    }
  }

  // Look for team name within the sprint name
  for (const teamName of teamNames) {
    const teamLower = teamName.toLowerCase()

    // Check if sprint contains the team name
    if (sprintLower.includes(teamLower)) {
      return teamName
    }

    // Handle special cases with spaces/hyphens/underscores
    const teamVariations = [
      teamLower.replace(/\s+/g, ''),           // Remove spaces: "SM Billing" -> "smbilling"
      teamLower.replace(/\s+/g, '-'),          // Replace spaces with hyphens: "SM Billing" -> "sm-billing"
      teamLower.replace(/\s+/g, '_'),          // Replace spaces with underscores: "SM Billing" -> "sm_billing"
      teamLower.replace(/-/g, ''),             // Remove hyphens: "CM-Refresh" -> "cmrefresh"
      teamLower.replace(/-/g, ' '),            // Replace hyphens with spaces: "CM-Refresh" -> "cm refresh"
      teamLower.replace(/_/g, ''),             // Remove underscores: "DevX_Infra" -> "devxinfra"
      teamLower.replace(/_/g, ' '),            // Replace underscores with spaces: "DevX_Infra" -> "devx infra"
      teamLower.replace(/_/g, '-')             // Replace underscores with hyphens: "DevX_Infra" -> "devx-infra"
    ]

    // Check all variations
    for (const variation of teamVariations) {
      if (sprintLower.includes(variation)) {
        return teamName
      }
    }
  }
  
  // If no team found, try to extract from common patterns like "TeamName - Something"
  const dashMatch = sprintName.match(/^([^-]+)\s*-/)
  if (dashMatch) {
    const extractedName = dashMatch[1].trim()

    // Check if extracted name matches any team (case insensitive)
    for (const teamName of teamNames) {
      if (teamName.toLowerCase() === extractedName.toLowerCase()) {
        return teamName
      }
    }

    // Return extracted name if it looks reasonable
    if (extractedName.length > 1) {
      return extractedName
    }
  }
  
  // Last resort: return the original sprint name
  return sprintName
}

/**
 * Get all unique team names for filter population
 * Now uses dynamic team configuration from backend API
 * @returns Promise<string[]> - Array of unique team names sorted alphabetically
 */
export async function getAllTeamNames(): Promise<string[]> {
  const teamNames = await getTeamNamesFromAPI()
  return [...teamNames].sort()
}

/**
 * Get unique teams from processed roadmap data
 * Now uses async team mapping
 * @param roadmapItems - Array of roadmap items with team info
 * @returns Promise<string[]> - Array of unique team names found in the data
 */
export async function getUniqueTeamsFromData(roadmapItems: any[]): Promise<string[]> {
  const teams = []
  for (const item of roadmapItems) {
    const teamName = item.teamName || await getTeamFromSprint(item.sprint || '')
    teams.push(teamName)
  }
  return [...new Set(teams)].filter(team => team && team.trim() !== '').sort()
}

/**
 * Debug function to test team mapping
 * Now uses async team mapping
 * @param sprintName - Sprint name to test
 * @returns Promise<object> - Object with sprint name and detected team
 */
export async function testTeamMapping(sprintName: string): Promise<{ sprintName: string; detectedTeam: string }> {
  return {
    sprintName: sprintName,
    detectedTeam: await getTeamFromSprint(sprintName)
  }
}

/**
 * Get all configured teams for validation
 * Now uses dynamic team configuration from backend API
 * @returns Promise<string[]> - Array of all configured team names
 */
export async function getConfiguredTeams(): Promise<string[]> {
  const teamNames = await getTeamNamesFromAPI()
  return [...teamNames].sort()
}

/**
 * Test function to see how all unique sprints in your data map to teams
 * Now uses async team mapping
 * Call this to debug mappings
 */
export async function testAllSprintMappings(uniqueSprints: string[]): Promise<Array<{ sprint: string; mappedTeam: string }>> {
  const results = []
  for (const sprint of uniqueSprints) {
    results.push({
      sprint: sprint,
      mappedTeam: await getTeamFromSprint(sprint)
    })
  }
  return results
}

/**
 * Synchronous fallback functions for backward compatibility
 * @deprecated Use async versions instead
 */
export function getAllTeamNamesSync(): string[] {
  console.warn('getAllTeamNamesSync is deprecated. Use async getAllTeamNames() instead.')
  return ['AI Services', 'AI/Search', 'Cloud IdM', 'CRDB', 'DBM', 'DevX_Infra', 'PC', 'RedisCore', 'ZTC']
}

export function getTeamFromSprintSync(sprintName: string): string {
  console.warn('getTeamFromSprintSync is deprecated. Use async getTeamFromSprint() instead.')

  if (!sprintName || typeof sprintName !== 'string') {
    return 'Unknown Team'
  }

  const sprintLower = sprintName.toLowerCase().trim()

  // Basic fallback patterns without API call
  if (sprintLower.includes('ai epic backlog')) return 'AI/Search'
  if (sprintLower.includes('ai services')) return 'AI Services'
  if (sprintLower.includes('dbm')) return 'DBM'
  if (sprintLower.includes('crdb')) return 'CRDB'
  if (sprintLower.includes('redis')) return 'RedisCore'

  return sprintName
}
